import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Switch } from 'antd';
import { formatNumber } from '@/utils/formatNumber';
import { Activity, TrendingUp } from 'lucide-react';

// Add type declaration for window.echarts
declare global {
  interface Window {
    echarts: any;
  }
}

export interface DailyData {
  datetime: string;
  outdoorTemp: number;
  acKwh: number;
  operation: string;
  indoorTemp: number;
  ahuWorkingPercent: number;
}

interface AutopilotGraphProps {
  data: DailyData[];
}

const calculateLinearRegression = (xData: number[], yData: number[]) => {
  const n = xData.length;
  if (n === 0) return { slope: 0, intercept: 0, r2: 0 };
  
  const sumX = xData.reduce((a, b) => a + b, 0);
  const sumY = yData.reduce((a, b) => a + b, 0);
  const sumXY = xData.reduce((sum, x, i) => sum + x * yData[i], 0);
  const sumX2 = xData.reduce((sum, x) => sum + x * x, 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  const meanY = sumY / n;
  const ssTotal = yData.reduce((sum, y) => sum + Math.pow(y - meanY, 2), 0);
  const ssResidual = yData.reduce((sum, y, i) => {
    const prediction = slope * xData[i] + intercept;
    return sum + Math.pow(y - prediction, 2);
  }, 0);
  const r2 = 1 - (ssResidual / ssTotal);
  
  return { slope, intercept, r2 };
};

export const AutopilotGraph: React.FC<AutopilotGraphProps> = ({ data }) => {
  const [isScatterMode, setIsScatterMode] = useState(true);
  const [showSecondaryLines, setShowSecondaryLines] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);

  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-light-grey p-4 h-full flex items-center justify-center">
        <p className="paragraph-12 text-dark-grey">No data available</p>
      </div>
    );
  }

  const { scatterData, barData, regressionData, barStats } = useMemo(() => {
    const cebcoData = data.filter(d => 
      d.operation === 'CEB-CO' && 
      !isNaN(d.outdoorTemp) && 
      !isNaN(d.acKwh) &&
      isFinite(d.outdoorTemp) &&
      isFinite(d.acKwh)
    );
    const noCebcoData = data.filter(d => 
      d.operation !== 'CEB-CO' &&
      !isNaN(d.outdoorTemp) && 
      !isNaN(d.acKwh) &&
      isFinite(d.outdoorTemp) &&
      isFinite(d.acKwh)
    );
    
    const cebcoX = cebcoData.map(d => d.outdoorTemp);
    const cebcoY = cebcoData.map(d => d.acKwh);
    const noCebcoX = noCebcoData.map(d => d.outdoorTemp);
    const noCebcoY = noCebcoData.map(d => d.acKwh);
    
    const cebcoRegression = calculateLinearRegression(cebcoX, cebcoY);
    const noCebcoRegression = calculateLinearRegression(noCebcoX, noCebcoY);
    
    const allTemps = data.map(d => d.outdoorTemp).filter(t => !isNaN(t) && isFinite(t));
    const tempRange = allTemps.length > 0 
      ? [Math.min(...allTemps), Math.max(...allTemps)]
      : [0, 40];
    
    // Prepare scatter data
    const scatterDataFormatted = [
      ...cebcoData.map(d => ({ ...d, type: 'CEB-CO' })),
      ...noCebcoData.map(d => ({ ...d, type: 'No CEB-CO' }))
    ];
    
    // Group data by date for bar chart
    const groupedByDate = data.reduce((acc, item) => {
      const date = item.datetime;
      if (!acc[date]) {
        acc[date] = {
          date,
          cebco: 0,
          noCebco: 0,
          indoorTemp: item.indoorTemp,
          outdoorTemp: item.outdoorTemp,
          ahuWorkingPercent: item.ahuWorkingPercent
        };
      }
      if (item.operation === 'CEB-CO') {
        acc[date].cebco = item.acKwh;
      } else {
        acc[date].noCebco = item.acKwh;
      }
      return acc;
    }, {} as Record<string, any>);
    
    const barDataGrouped = Object.values(groupedByDate).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    // Calculate bar chart statistics
    const cebcoDays = barDataGrouped.filter(d => d.cebco > 0).length;
    const noCebcoDays = barDataGrouped.filter(d => d.noCebco > 0).length;
    const totalCebco = barDataGrouped.reduce((sum, d) => sum + d.cebco, 0);
    const totalNoCebco = barDataGrouped.reduce((sum, d) => sum + d.noCebco, 0);
    const avgCebco = cebcoDays > 0 ? totalCebco / cebcoDays : 0;
    const avgNoCebco = noCebcoDays > 0 ? totalNoCebco / noCebcoDays : 0;
    
    // Calculate average AHU % for each group
    const cebcoAhuData = barDataGrouped.filter(d => d.cebco > 0);
    const noCebcoAhuData = barDataGrouped.filter(d => d.noCebco > 0);
    const avgCebcoAhu = cebcoAhuData.length > 0 
      ? cebcoAhuData.reduce((sum, d) => sum + d.ahuWorkingPercent, 0) / cebcoAhuData.length 
      : 0;
    const avgNoCebcoAhu = noCebcoAhuData.length > 0 
      ? noCebcoAhuData.reduce((sum, d) => sum + d.ahuWorkingPercent, 0) / noCebcoAhuData.length 
      : 0;
    
    return {
      scatterData: scatterDataFormatted,
      barData: barDataGrouped,
      regressionData: {
        cebco: cebcoRegression,
        noCebco: noCebcoRegression,
        tempRange
      },
      barStats: {
        cebco: {
          days: cebcoDays,
          total: totalCebco,
          average: avgCebco,
          avgAhu: avgCebcoAhu
        },
        noCebco: {
          days: noCebcoDays,
          total: totalNoCebco,
          average: avgNoCebco,
          avgAhu: avgNoCebcoAhu
        }
      }
    };
  }, [data]);

  const initializeChart = () => {
    if (chartRef.current && window.echarts) {
      chartInstance.current = window.echarts.init(chartRef.current);
      updateChart();
    }
  };

  const updateChart = () => {
    if (!chartInstance.current) return;

    // Clear the chart completely before rendering new content
    chartInstance.current.clear();

    if (isScatterMode) {
      // Scatter plot mode
      const cebcoScatter = scatterData
        .filter(d => d.type === 'CEB-CO')
        .map(d => [d.outdoorTemp, d.acKwh, d.datetime]);
      
      const noCebcoScatter = scatterData
        .filter(d => d.type === 'No CEB-CO')
        .map(d => [d.outdoorTemp, d.acKwh, d.datetime]);

      // Generate regression lines
      const minTemp = Math.min(...scatterData.map(d => d.outdoorTemp));
      const maxTemp = Math.max(...scatterData.map(d => d.outdoorTemp));
      const tempStep = (maxTemp - minTemp) / 50;
      
      const cebcoLine = [];
      const noCebcoLine = [];
      
      for (let temp = minTemp; temp <= maxTemp; temp += tempStep) {
        cebcoLine.push([temp, regressionData.cebco.slope * temp + regressionData.cebco.intercept]);
        noCebcoLine.push([temp, regressionData.noCebco.slope * temp + regressionData.noCebco.intercept]);
      }

      const option = {
        grid: {
          left: '3%',
          right: '5%',
          bottom: '12%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          name: 'Outdoor Temperature (°C)',
          nameLocation: 'middle',
          nameGap: 30,
          min: 25,
          axisLabel: {
            formatter: (value: number) => formatNumber(value, 1)
          }
        },
        yAxis: {
          type: 'value',
          name: 'AC Consumption (kWh)',
          nameLocation: 'middle',
          nameGap: 50
        },
        legend: {
          data: [
            { name: 'CEB-CO', itemStyle: { color: '#0E7EE4' } },
            { name: 'No CEB-CO', itemStyle: { color: '#808080' } },
            { name: 'CEB-CO Trend', itemStyle: { color: '#0E7EE4' } },
            { name: 'No CEB-CO Trend', itemStyle: { color: '#808080' } }
          ],
          top: 0,
          selected: {
            'CEB-CO': true,
            'No CEB-CO': true,
            'CEB-CO Trend': true,
            'No CEB-CO Trend': true
          }
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: 'rgba(0, 0, 0, 0.1)',
          borderWidth: 1,
          textStyle: {
            color: '#333',
            fontSize: 12
          },
          formatter: function(params: any) {
            if (params.seriesType === 'scatter') {
              const [temp, kwh, date] = params.data;
              return `<div style="padding: 8px;">
                <div style="font-weight: bold; margin-bottom: 6px; color: #1f2937;">${date}</div>
                <div style="margin-bottom: 3px;">
                  <span style="color: #6b7280;">Temperature:</span> 
                  <span style="font-weight: 600; color: #374151;">${formatNumber(temp, 1)}°C</span>
                </div>
                <div style="margin-bottom: 3px;">
                  <span style="color: #6b7280;">Type:</span> 
                  <span style="font-weight: 600; color: ${params.seriesName === 'CEB-CO' ? '#0E7EE4' : '#808080'};">${params.seriesName}</span>
                </div>
                <div>
                  <span style="color: #6b7280;">Consumption:</span> 
                  <span style="font-weight: 600; color: #374151;">${formatNumber(kwh, 0)} kWh</span>
                </div>
              </div>`;
            } else if (params.seriesType === 'line') {
              const temp = params.data[0];
              const kwh = params.data[1];
              return `<div style="padding: 8px;">
                <div style="font-weight: bold; margin-bottom: 6px; color: #1f2937;">${params.seriesName}</div>
                <div style="margin-bottom: 3px;">
                  <span style="color: #6b7280;">Temperature:</span> 
                  <span style="font-weight: 600; color: #374151;">${formatNumber(temp, 1)}°C</span>
                </div>
                <div>
                  <span style="color: #6b7280;">Predicted:</span> 
                  <span style="font-weight: 600; color: #374151;">${formatNumber(kwh, 1)} kWh</span>
                </div>
              </div>`;
            }
            return '';
          }
        },
        series: [
          {
            name: 'CEB-CO',
            type: 'scatter',
            data: cebcoScatter,
            itemStyle: {
              color: '#0E7EE4',
              opacity: 0.7
            },
            symbolSize: 8
          },
          {
            name: 'No CEB-CO',
            type: 'scatter',
            data: noCebcoScatter,
            itemStyle: {
              color: '#808080',
              opacity: 0.7
            },
            symbolSize: 8
          },
          {
            name: 'CEB-CO Trend',
            type: 'line',
            data: cebcoLine,
            lineStyle: {
              color: '#0E7EE4',
              width: 2,
              type: 'dashed'
            },
            symbol: 'none',
            smooth: true
          },
          {
            name: 'No CEB-CO Trend',
            type: 'line',
            data: noCebcoLine,
            lineStyle: {
              color: '#808080',
              width: 2,
              type: 'dashed'
            },
            symbol: 'none',
            smooth: true
          }
        ]
      };

      chartInstance.current.setOption(option);
    } else {
      // Bar chart mode
      const dates = barData.map(d => d.date);
      const cebcoData = barData.map(d => d.cebco);
      const noCebcoData = barData.map(d => d.noCebco);
      const ahuData = barData.map(d => d.ahuWorkingPercent);

      const series = [
        {
          name: 'CEB-CO',
          type: 'bar',
          data: cebcoData,
          itemStyle: {
            color: '#0E7EE4'
          },
          barWidth: '40%',
          barGap: '20%'
        },
        {
          name: 'No CEB-CO',
          type: 'bar',
          data: noCebcoData,
          itemStyle: {
            color: '#808080'
          },
          barWidth: '40%'
        }
      ];

      if (showSecondaryLines) {
        series.push({
          name: 'AHU Working %',
          type: 'line',
          yAxisIndex: 1,
          data: ahuData,
          lineStyle: {
            color: '#ef4444',
            width: 2
          },
          itemStyle: {
            color: '#ef4444'  // red color for legend icon
          },
          symbol: 'none'
        } as any);
      }

      const option = {
        grid: {
          left: '3%',
          right: showSecondaryLines ? '5%' : '3%',
          bottom: '15%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            rotate: 45,
            fontSize: 10
          }
        },
        yAxis: [
          {
            type: 'value',
            name: 'AC Consumption (kWh)',
            nameLocation: 'middle',
            nameGap: 50
          },
          ...(showSecondaryLines ? [{
            type: 'value',
            name: 'AHU Working (%)',
            nameLocation: 'middle',
            nameGap: 50,
            position: 'right'
          }] : [])
        ],
        legend: {
          data: showSecondaryLines ? [
            'CEB-CO', 
            'No CEB-CO', 
            {
              name: 'AHU Working %',
              textStyle: {
                color: '#ef4444'  // red color
              }
            }
          ] : ['CEB-CO', 'No CEB-CO'],
          top: 0
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params: any) {
            let html = `<div><strong>${params[0].name}</strong><br/>`;
            params.forEach((param: any) => {
              const unit = param.seriesName.includes('%') ? '%' : ' kWh';
              html += `${param.seriesName}: ${formatNumber(param.value, 1)}${unit}<br/>`;
            });
            html += '</div>';
            return html;
          }
        },
        series: series
      };

      chartInstance.current.setOption(option);
    }

    // Handle legend toggle
    chartInstance.current.off('legendselectchanged');
    chartInstance.current.on('legendselectchanged', function (params: any) {
      // ECharts handles the show/hide automatically
    });
  };

  // Load ECharts
  useEffect(() => {
    if (!window.echarts) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js';
      script.async = true;
      script.onload = initializeChart;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    } else {
      initializeChart();
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, []);

  // Update chart when data or settings change
  useEffect(() => {
    if (chartInstance.current) {
      updateChart();
    }
  }, [isScatterMode, showSecondaryLines, scatterData, barData, regressionData, barStats]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-light-grey p-4 h-full flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-3 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="h-[18px] w-[18px] bg-light-grey rounded flex items-center justify-center">
            <Activity className="h-3 w-3 text-primary" />
          </div>
          <h3 className="text-dark-blue heading-7">Autopilot Analysis</h3>
        </div>
        <div className="flex items-center gap-4">
          {!isScatterMode && (
            <label className="flex items-center gap-2">
              <Switch
                checked={showSecondaryLines}
                onChange={setShowSecondaryLines}
                size="small"
              />
              <span className="paragraph-12 text-dark-grey">Show AHU %</span>
            </label>
          )}
          <label className="flex items-center gap-2">
            <span className="paragraph-12 text-dark-grey">Scatter</span>
            <Switch
              checked={!isScatterMode}
              onChange={(checked) => setIsScatterMode(!checked)}
            />
            <span className="paragraph-12 text-dark-grey">Bar</span>
          </label>
        </div>
      </div>
      
      {isScatterMode && (
        <div className="space-y-1 mb-2 p-2 bg-bg rounded-md">
          <div className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3 text-primary" />
            <span className="paragraph-10 text-dark-grey">
              R² (CEB-CO): <span className="font-medium text-black">{formatNumber(regressionData.cebco.r2, 3)}</span> | 
              R² (No CEB-CO): <span className="font-medium text-black">{formatNumber(regressionData.noCebco.r2, 3)}</span>
            </span>
          </div>
          <div className="paragraph-9 text-dark-grey">
            <span style={{ color: '#0E7EE4' }}>CEB-CO:</span> y = {formatNumber(regressionData.cebco.slope, 2)}x + {formatNumber(regressionData.cebco.intercept, 2)}
          </div>
          <div className="paragraph-9 text-dark-grey">
            <span className="text-gray-500">No CEB-CO:</span> y = {formatNumber(regressionData.noCebco.slope, 2)}x + {formatNumber(regressionData.noCebco.intercept, 2)}
          </div>
        </div>
      )}
      
      {!isScatterMode && (
        <div className="space-y-1 mb-2 p-2 bg-bg rounded-md">
          <div className="flex items-center gap-1 mb-1">
            <Activity className="h-3 w-3 text-primary" />
            <span className="paragraph-10 font-medium text-dark-grey">Consumption Summary</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* CEB-CO Column */}
            <div>
              <div className="paragraph-9 text-dark-grey mb-1">
                <span style={{ color: '#0E7EE4' }} className="font-medium">CEB-CO</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="paragraph-9 text-dark-grey">Days: <span className="font-medium text-black">{barStats.cebco.days}</span></span>
                  <span className="paragraph-9 text-dark-grey">Avg AHU: <span className="font-medium text-black">{formatNumber(barStats.cebco.avgAhu, 1)}%</span></span>
                </div>
                <div className="flex justify-between">
                  <span className="paragraph-9 text-dark-grey">Total: <span className="font-medium text-black">{formatNumber(barStats.cebco.total, 0)} kWh</span></span>
                  <span className="paragraph-9 text-dark-grey">Average: <span className="font-medium text-black">{formatNumber(barStats.cebco.average, 1)} kWh/day</span></span>
                </div>
              </div>
            </div>
            
            {/* No CEB-CO Column */}
            <div>
              <div className="paragraph-9 text-dark-grey mb-1">
                <span className="text-gray-600 font-medium">No CEB-CO</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="paragraph-9 text-dark-grey">Days: <span className="font-medium text-black">{barStats.noCebco.days}</span></span>
                  <span className="paragraph-9 text-dark-grey">Avg AHU: <span className="font-medium text-black">{formatNumber(barStats.noCebco.avgAhu, 1)}%</span></span>
                </div>
                <div className="flex justify-between">
                  <span className="paragraph-9 text-dark-grey">Total: <span className="font-medium text-black">{formatNumber(barStats.noCebco.total, 0)} kWh</span></span>
                  <span className="paragraph-9 text-dark-grey">Average: <span className="font-medium text-black">{formatNumber(barStats.noCebco.average, 1)} kWh/day</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div ref={chartRef} className="flex-1" style={{ minHeight: '330px' }} />
    </div>
  );
};